import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

/* ─── sample data for live preview ─── */
const SAMPLE_VARS = {
  student_name: 'John Doe',
  course_name: 'Introduction to Computer Science',
  program_name: 'B.Tech Computer Science',
  academic_year: '2026',
  exam_marks: '85/100',
  assignment_marks: '90/100',
  final_marks: 'A+ (92%)',
  issue_date: '05 March 2026',
  certificate_number: 'LLS-2026-00001',
  date: '05 March 2026',
};

const FONT_FAMILIES = ['Arial', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New', 'Trebuchet MS', 'Palatino Linotype', 'Garamond', 'Bookman Old Style', 'Comic Sans MS'];
const PLACEHOLDER_VARS = ['{{student_name}}', '{{course_name}}', '{{program_name}}', '{{academic_year}}', '{{exam_marks}}', '{{assignment_marks}}', '{{final_marks}}', '{{issue_date}}', '{{certificate_number}}', '{{date}}'];

/* ─── helper: render objects → preview HTML ─── */
function buildPreviewHtml(objects, scale = 0.55, bgColor = '#ffffff') {
  let html = '';
  for (const obj of objects) {
    if (obj.type === 'rect') {
      html += `<div style="position:absolute;left:${obj.left||0}px;top:${obj.top||0}px;width:${obj.width||100}px;height:${obj.height||100}px;background:${obj.fill||'transparent'};border:${obj.strokeWidth||0}px solid ${obj.stroke||'none'};border-radius:${obj.rx||0}px;"></div>`;
    } else if (['i-text','text','textbox'].includes(obj.type)) {
      let t = obj.text || '';
      for (const [k,v] of Object.entries(SAMPLE_VARS)) t = t.replace(`{{${k}}}`,v).replace(`{${k}}`,v);
      const tr = obj.originX==='center'?'transform:translateX(-50%);':obj.originX==='right'?'transform:translateX(-100%);':'';
      const fs = obj.fontStyle === 'italic' ? 'font-style:italic;' : '';
      const td = obj.underline ? 'text-decoration:underline;' : '';
      html += `<div style="position:absolute;left:${obj.left||0}px;top:${obj.top||0}px;font-size:${obj.fontSize||16}px;font-family:${obj.fontFamily||'Arial'};font-weight:${obj.fontWeight||'normal'};color:${obj.fill||'#000'};text-align:${obj.textAlign||'left'};${tr}${fs}${td}white-space:nowrap;">${t.replace(/</g,'&lt;')}</div>`;
    }
  }
  return `<div style="position:relative;width:1000px;height:700px;background:${bgColor || '#ffffff'};box-shadow:0 4px 20px rgba(0,0,0,0.12);overflow:hidden;transform-origin:top left;transform:scale(${scale});">${html}</div>`;
}

/* ─── small reusable input styles ─── */
const inputS = { padding:'6px 10px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'13px', width:'100%', boxSizing:'border-box' };
const labelS = { fontSize:'11px', fontWeight:600, color:'#6b7280', marginBottom:'2px', display:'block' };
const btnSmall = (bg,fg,border) => ({ padding:'5px 12px', background:bg, color:fg, border:border?`1px solid ${border}`:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:600 });

/* ═══════════════════════════════════════════════════
   EDIT MODAL  – visual property editor + live preview
   ═══════════════════════════════════════════════════ */
const EditModal = ({ layout, onClose, onSaved }) => {
  const [layoutName, setLayoutName] = useState(layout.layout_name);
  const [objects, setObjects] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [certificateBgColor, setCertificateBgColor] = useState('#ffffff');

  useEffect(() => {
    try {
      const c = JSON.parse(layout.template_content || '{}');
      setObjects(c.objects || []);
      setCertificateBgColor(c.background || '#ffffff');
    } catch { setObjects([]); }
  }, [layout]);

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),2500); };

  /* update a single property of one object */
  const updateObj = (idx, key, value) => {
    setObjects(prev => prev.map((o,i) => i===idx ? { ...o, [key]: value } : o));
  };

  /* add a new text element */
  const addTextElement = () => {
    setObjects(prev => [...prev, { type:'i-text', left:100, top:100, text:'New Text', fontSize:20, fontFamily:'Arial', fontWeight:'normal', fill:'#000000', textAlign:'left', originX:'left' }]);
    setSelectedIdx(objects.length);
  };

  /* add a rectangle element */
  const addRectElement = () => {
    setObjects(prev => [...prev, { type:'rect', left:50, top:50, width:200, height:100, fill:'', stroke:'#333333', strokeWidth:2, rx:0, ry:0 }]);
    setSelectedIdx(objects.length);
  };

  /* delete an element */
  const deleteElement = (idx) => {
    setObjects(prev => prev.filter((_,i)=>i!==idx));
    setSelectedIdx(null);
  };

  /* move element up/down in z-order */
  const moveElement = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= objects.length) return;
    setObjects(prev => {
      const a = [...prev];
      [a[idx], a[newIdx]] = [a[newIdx], a[idx]];
      return a;
    });
    setSelectedIdx(newIdx);
  };

  /* duplicate element */
  const duplicateElement = (idx) => {
    const copy = { ...objects[idx], left: (objects[idx].left||0) + 20, top: (objects[idx].top||0) + 20 };
    setObjects(prev => [...prev.slice(0, idx+1), copy, ...prev.slice(idx+1)]);
    setSelectedIdx(idx + 1);
  };

  /* save to backend */
  const handleSave = async () => {
    if (!layoutName.trim()) { showToast('Name is required','error'); return; }
    setSaving(true);
    try {
      const content = JSON.stringify({ version: '5.3.0', objects, background: certificateBgColor });
      await axios.put(`/api/certificate-layouts/${layout.id}`, {
        layout_name: layoutName,
        template_content: content,
      });
      showToast('Layout saved!');
      setTimeout(() => { onSaved(); }, 600);
    } catch (e) {
      showToast('Save failed','error');
    } finally { setSaving(false); }
  };

  const previewHtml = useMemo(() => buildPreviewHtml(objects, 0.52, certificateBgColor), [objects, certificateBgColor]);
  const sel = selectedIdx !== null ? objects[selectedIdx] : null;
  const isText = sel && ['i-text','text','textbox'].includes(sel.type);
  const isRect = sel && sel.type === 'rect';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#f9fafb', borderRadius:'16px', width:'95vw', maxWidth:'1280px', height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>

        {/* header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 24px', borderBottom:'1px solid #e5e7eb', background:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px', flex:1 }}>
            <span style={{ fontSize:'20px' }}>✏️</span>
            <input value={layoutName} onChange={e=>setLayoutName(e.target.value)}
              style={{ fontSize:'18px', fontWeight:700, border:'1px solid transparent', borderRadius:'8px', padding:'6px 12px', flex:1, maxWidth:400, outline:'none', transition:'border .2s' }}
              onFocus={e=>e.target.style.borderColor='#2563eb'} onBlur={e=>e.target.style.borderColor='transparent'}
            />
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={onClose} style={btnSmall('#f3f4f6','#374151','#d1d5db')}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...btnSmall(saving?'#93c5fd':'#2563eb','#fff'), padding:'8px 24px', fontSize:'14px' }}>
              {saving ? '⏳ Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </div>

        {/* body: sidebar + preview */}
        <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

          {/* ── left panel: element list + properties ── */}
          <div style={{ width:'340px', minWidth:'300px', borderRight:'1px solid #e5e7eb', background:'#fff', display:'flex', flexDirection:'column', overflow:'hidden' }}>

            {/* certificate background color */}
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb' }}>
              <label style={{ fontSize:'11px', fontWeight:700, color:'#374151', display:'block', marginBottom:'6px' }}>🎨 Certificate Background Color</label>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <input type="color" value={certificateBgColor} onChange={e=>setCertificateBgColor(e.target.value)}
                  style={{ width:'36px', height:'30px', padding:'2px', border:'1px solid #d1d5db', borderRadius:'6px', cursor:'pointer' }} />
                <input type="text" value={certificateBgColor} onChange={e=>setCertificateBgColor(e.target.value)}
                  style={{ flex:1, padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'12px', fontFamily:'monospace' }} />
                <button onClick={()=>setCertificateBgColor('#ffffff')} title="Reset to white"
                  style={{ padding:'4px 8px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:'6px', cursor:'pointer', fontSize:'11px', color:'#6b7280' }}>Reset</button>
              </div>
              <div style={{ display:'flex', gap:'4px', marginTop:'6px', flexWrap:'wrap' }}>
                {['#ffffff','#eff6ff','#f0fdf4','#fefce8','#fdf2f8','#faf5ff','#fff7ed','#f5f5f4','#1e293b','#0f172a'].map(c=>(
                  <button key={c} onClick={()=>setCertificateBgColor(c)} title={c}
                    style={{ width:'22px', height:'22px', borderRadius:'4px', border: certificateBgColor===c ? '2px solid #2563eb' : '1px solid #d1d5db', background:c, cursor:'pointer', padding:0 }} />
                ))}
              </div>
            </div>

            {/* element list */}
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:'13px', fontWeight:700, color:'#374151' }}>Elements ({objects.length})</span>
              <div style={{ display:'flex', gap:'4px' }}>
                <button onClick={addTextElement} title="Add text" style={btnSmall('#eff6ff','#2563eb','#bfdbfe')}>+ Text</button>
                <button onClick={addRectElement} title="Add rectangle" style={btnSmall('#fef3c7','#92400e','#fde68a')}>+ Rect</button>
              </div>
            </div>
            <div style={{ flex:'0 0 auto', maxHeight:'200px', overflowY:'auto', borderBottom:'1px solid #e5e7eb' }}>
              {objects.map((obj, idx) => {
                const isTextObj = ['i-text','text','textbox'].includes(obj.type);
                const label = isTextObj ? (obj.text||'').substring(0,28) : `Rectangle`;
                return (
                  <div key={idx} onClick={() => setSelectedIdx(idx)}
                    style={{ padding:'8px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px',
                      background: selectedIdx===idx ? '#eff6ff' : 'transparent',
                      borderLeft: selectedIdx===idx ? '3px solid #2563eb' : '3px solid transparent',
                      transition:'background .15s' }}
                  >
                    <span style={{ fontSize:'14px' }}>{isTextObj ? '🔤' : '▭'}</span>
                    <span style={{ fontSize:'13px', color:'#374151', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
                    <span style={{ fontSize:'11px', color:'#9ca3af' }}>#{idx+1}</span>
                  </div>
                );
              })}
              {objects.length === 0 && <div style={{ padding:'20px', textAlign:'center', color:'#9ca3af', fontSize:'13px' }}>No elements — add text or rect above</div>}
            </div>

            {/* properties panel */}
            <div style={{ flex:1, overflowY:'auto', padding:'16px' }}>
              {sel === null ? (
                <div style={{ textAlign:'center', color:'#9ca3af', fontSize:'13px', marginTop:'40px' }}>
                  <div style={{ fontSize:'32px', marginBottom:'8px' }}>👆</div>
                  Select an element to edit its properties
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                  {/* element actions toolbar */}
                  <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', paddingBottom:'8px', borderBottom:'1px solid #f3f4f6' }}>
                    <button onClick={()=>moveElement(selectedIdx,-1)} disabled={selectedIdx===0} title="Move back" style={btnSmall('#f3f4f6','#374151','#d1d5db')}>↓ Back</button>
                    <button onClick={()=>moveElement(selectedIdx,1)} disabled={selectedIdx===objects.length-1} title="Move front" style={btnSmall('#f3f4f6','#374151','#d1d5db')}>↑ Front</button>
                    <button onClick={()=>duplicateElement(selectedIdx)} title="Duplicate" style={btnSmall('#f0fdf4','#059669','#bbf7d0')}>⧉ Dup</button>
                    <button onClick={()=>deleteElement(selectedIdx)} title="Delete" style={btnSmall('#fef2f2','#dc2626','#fecaca')}>🗑</button>
                  </div>

                  {/* position */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    <div><label style={labelS}>Left (X)</label><input type="number" value={sel.left||0} onChange={e=>updateObj(selectedIdx,'left',+e.target.value)} style={inputS}/></div>
                    <div><label style={labelS}>Top (Y)</label><input type="number" value={sel.top||0} onChange={e=>updateObj(selectedIdx,'top',+e.target.value)} style={inputS}/></div>
                  </div>

                  {isRect && (
                    <>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                        <div><label style={labelS}>Width</label><input type="number" value={sel.width||100} onChange={e=>updateObj(selectedIdx,'width',+e.target.value)} style={inputS}/></div>
                        <div><label style={labelS}>Height</label><input type="number" value={sel.height||100} onChange={e=>updateObj(selectedIdx,'height',+e.target.value)} style={inputS}/></div>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                        <div><label style={labelS}>Fill</label><input type="color" value={sel.fill||'#ffffff'} onChange={e=>updateObj(selectedIdx,'fill',e.target.value)} style={{ ...inputS, padding:'2px 4px', height:'32px' }}/></div>
                        <div><label style={labelS}>Stroke</label><input type="color" value={sel.stroke||'#000000'} onChange={e=>updateObj(selectedIdx,'stroke',e.target.value)} style={{ ...inputS, padding:'2px 4px', height:'32px' }}/></div>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                        <div><label style={labelS}>Stroke Width</label><input type="number" value={sel.strokeWidth||0} min={0} onChange={e=>updateObj(selectedIdx,'strokeWidth',+e.target.value)} style={inputS}/></div>
                        <div><label style={labelS}>Border Radius</label><input type="number" value={sel.rx||0} min={0} onChange={e=>{updateObj(selectedIdx,'rx',+e.target.value);updateObj(selectedIdx,'ry',+e.target.value);}} style={inputS}/></div>
                      </div>
                    </>
                  )}

                  {isText && (
                    <>
                      <div>
                        <label style={labelS}>Text Content</label>
                        <textarea value={sel.text||''} onChange={e=>updateObj(selectedIdx,'text',e.target.value)} rows={2}
                          style={{ ...inputS, resize:'vertical', fontFamily:'monospace' }}/>
                        <div style={{ display:'flex', gap:'4px', marginTop:'4px', flexWrap:'wrap' }}>
                          {PLACEHOLDER_VARS.map(v=>(
                            <button key={v} onClick={()=>updateObj(selectedIdx,'text',(sel.text||'')+v)}
                              style={{ padding:'2px 6px', fontSize:'10px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:'4px', cursor:'pointer', color:'#6b7280' }}>{v.replace(/[{}]/g,'')}</button>
                          ))}
                        </div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                        <div>
                          <label style={labelS}>Font Family</label>
                          <select value={sel.fontFamily||'Arial'} onChange={e=>updateObj(selectedIdx,'fontFamily',e.target.value)} style={inputS}>
                            {FONT_FAMILIES.map(f=><option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                        <div><label style={labelS}>Font Size</label><input type="number" value={sel.fontSize||16} min={8} max={120} onChange={e=>updateObj(selectedIdx,'fontSize',+e.target.value)} style={inputS}/></div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                        <div>
                          <label style={labelS}>Weight</label>
                          <select value={sel.fontWeight||'normal'} onChange={e=>updateObj(selectedIdx,'fontWeight',e.target.value)} style={inputS}>
                            <option value="normal">Normal</option><option value="bold">Bold</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelS}>Style</label>
                          <select value={sel.fontStyle||'normal'} onChange={e=>updateObj(selectedIdx,'fontStyle',e.target.value)} style={inputS}>
                            <option value="normal">Normal</option><option value="italic">Italic</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelS}>Align</label>
                          <select value={sel.textAlign||'left'} onChange={e=>updateObj(selectedIdx,'textAlign',e.target.value)} style={inputS}>
                            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                        <div><label style={labelS}>Color</label><input type="color" value={sel.fill||'#000000'} onChange={e=>updateObj(selectedIdx,'fill',e.target.value)} style={{ ...inputS, padding:'2px 4px', height:'32px' }}/></div>
                        <div>
                          <label style={labelS}>Origin X</label>
                          <select value={sel.originX||'left'} onChange={e=>updateObj(selectedIdx,'originX',e.target.value)} style={inputS}>
                            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display:'flex', gap:'8px' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'12px', cursor:'pointer' }}>
                          <input type="checkbox" checked={!!sel.underline} onChange={e=>updateObj(selectedIdx,'underline',e.target.checked)} /> Underline
                        </label>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── right panel: live preview ── */}
          <div style={{ flex:1, overflow:'auto', padding:'24px', background:'#f1f5f9', display:'flex', flexDirection:'column', alignItems:'center' }}>
            <div style={{ fontSize:'12px', fontWeight:600, color:'#6b7280', marginBottom:'12px' }}>LIVE PREVIEW (sample data)</div>
            <div style={{ overflow:'auto', border:'1px solid #e2e8f0', borderRadius:'12px', background:'#fff', padding:'12px' }}
              dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>

        {/* toast inside modal */}
        {toast && <div style={{ position:'absolute', bottom:'16px', right:'16px', padding:'10px 20px', background: toast.type==='error'?'#ef4444':'#059669', color:'#fff', borderRadius:'8px', fontSize:'13px', fontWeight:600, zIndex:1010 }}>{toast.msg}</div>}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   MAIN COMPONENT – list / create / seed / preview / edit
   ═══════════════════════════════════════════════════ */
const CertificateManagement = () => {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [previewLayout, setPreviewLayout] = useState(null);
  const [editLayout, setEditLayout] = useState(null);
  const [toast, setToast] = useState(null);

  const loadLayouts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/certificate-layouts');
      setLayouts(res.data || []);
    } catch (err) { console.error('Error loading layouts:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLayouts(); }, [loadLayouts]);

  const showToast = (message, type = 'success') => { setToast({ message, type }); setTimeout(() => setToast(null), 3000); };

  const handleSeedTemplates = async () => {
    try { const r = await axios.post('/api/certificate-templates/seed'); showToast(r.data.message || 'Templates seeded!'); loadLayouts(); }
    catch { showToast('Failed to seed templates','error'); }
  };

  const handleSetDefault = async (layout) => {
    try { await axios.put(`/api/certificate-layouts/${layout.id}`, { is_default: true }); showToast(`${layout.layout_name} set as default`); loadLayouts(); }
    catch { showToast('Failed to set default','error'); }
  };

  const handleDelete = async (layout) => {
    if (!window.confirm(`Delete layout "${layout.layout_name}"?`)) return;
    try { await axios.delete(`/api/certificate-layouts/${layout.id}`); showToast('Layout deleted'); loadLayouts(); }
    catch { showToast('Failed to delete layout','error'); }
  };

  const handleCreate = async () => {
    if (!newLayoutName.trim()) return;
    try {
      const defaultContent = JSON.stringify({ version:'5.3.0', objects:[
        { type:'rect', left:50, top:50, width:900, height:600, fill:'', stroke:'#2c3e50', strokeWidth:4, rx:12, ry:12 },
        { type:'i-text', left:500, top:130, text:'CERTIFICATE', fontSize:38, fontFamily:'Georgia', fontWeight:'bold', fill:'#2c3e50', textAlign:'center', originX:'center' },
        { type:'i-text', left:500, top:250, text:'{{student_name}}', fontSize:46, fontFamily:'Times New Roman', fontWeight:'bold', fill:'#1f2937', textAlign:'center', originX:'center' },
        { type:'i-text', left:500, top:340, text:'{{course_name}}', fontSize:26, fontFamily:'Arial', fontWeight:'bold', fill:'#2563eb', textAlign:'center', originX:'center' },
        { type:'i-text', left:500, top:420, text:'Grade: {{final_marks}}', fontSize:22, fontFamily:'Georgia', fill:'#374151', textAlign:'center', originX:'center' },
        { type:'i-text', left:110, top:600, text:'{{issue_date}}', fontSize:14, fontFamily:'Arial', fill:'#6b7280' },
        { type:'i-text', left:730, top:600, text:'{{certificate_number}}', fontSize:14, fontFamily:'Arial', fill:'#6b7280' },
      ]});
      await axios.post('/api/certificate-layouts', { layout_name: newLayoutName, template_content: defaultContent, is_default: false });
      showToast('Layout created'); setShowCreate(false); setNewLayoutName(''); loadLayouts();
    } catch { showToast('Failed to create layout','error'); }
  };

  const previewHtml = useMemo(() => {
    if (!previewLayout) return '';
    try {
      const c = JSON.parse(previewLayout.template_content||'{}');
      return buildPreviewHtml(c.objects||[], 0.6, c.background || '#ffffff');
    } catch { return '<div style="padding:20px;color:#ef4444;">Failed to parse template</div>'; }
  }, [previewLayout]);

  return (
    <div style={{ padding:'20px', maxWidth:'1200px', margin:'0 auto' }}>
      {/* header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ margin:0, color:'#1f2937' }}>📜 Certificate Layouts</h2>
          <p style={{ color:'#6b7280', margin:'4px 0 0' }}>Manage certificate templates used for generating student certificates</p>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={handleSeedTemplates} style={{ padding:'10px 18px', background:'#8b5cf6', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:600 }}>
            🌱 Seed Default Templates
          </button>
          <button onClick={()=>setShowCreate(true)} style={{ padding:'10px 18px', background:'#2563eb', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:600 }}>
            + Create New Layout
          </button>
        </div>
      </div>

      {/* create dialog */}
      {showCreate && (
        <div style={{ background:'#f0f9ff', border:'1px solid #bfdbfe', borderRadius:'10px', padding:'20px', marginBottom:'20px' }}>
          <h4 style={{ margin:'0 0 12px' }}>Create New Layout</h4>
          <div style={{ display:'flex', gap:'10px' }}>
            <input type="text" placeholder="Layout name..." value={newLayoutName} onChange={e=>setNewLayoutName(e.target.value)}
              style={{ flex:1, padding:'10px 14px', border:'1px solid #d1d5db', borderRadius:'8px', fontSize:'14px' }}
              onKeyDown={e=>e.key==='Enter'&&handleCreate()} />
            <button onClick={handleCreate} style={{ padding:'10px 20px', background:'#059669', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer' }}>Create</button>
            <button onClick={()=>{setShowCreate(false);setNewLayoutName('');}} style={{ padding:'10px 20px', background:'#6b7280', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* grid */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#6b7280' }}>Loading layouts...</div>
      ) : layouts.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px', background:'#f9fafb', borderRadius:'12px', border:'2px dashed #d1d5db' }}>
          <div style={{ fontSize:'48px', marginBottom:'12px' }}>📜</div>
          <h3 style={{ color:'#374151' }}>No Certificate Layouts</h3>
          <p style={{ color:'#6b7280' }}>Click "Seed Default Templates" to add pre-built templates, or create your own.</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:'20px' }}>
          {layouts.map(layout => (
            <div key={layout.id} style={{ background:'#fff', borderRadius:'12px', border:layout.is_default?'2px solid #059669':'1px solid #e5e7eb', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ padding:'20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                  <h3 style={{ margin:0, fontSize:'16px', color:'#1f2937' }}>{layout.layout_name}</h3>
                  {layout.is_default && <span style={{ background:'#d1fae5', color:'#065f46', padding:'2px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:600 }}>Default</span>}
                </div>
                <p style={{ color:'#9ca3af', fontSize:'13px', margin:'0 0 12px' }}>Created: {layout.created_at ? new Date(layout.created_at).toLocaleDateString() : 'N/A'}</p>
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  <button onClick={()=>setPreviewLayout(layout)} style={{ padding:'6px 14px', background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>👁 Preview</button>
                  <button onClick={()=>setEditLayout(layout)} style={{ padding:'6px 14px', background:'#fefce8', color:'#a16207', border:'1px solid #fef08a', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>✏️ Edit</button>
                  {!layout.is_default && <button onClick={()=>handleSetDefault(layout)} style={{ padding:'6px 14px', background:'#f0fdf4', color:'#059669', border:'1px solid #bbf7d0', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>✓ Set Default</button>}
                  <button onClick={()=>handleDelete(layout)} style={{ padding:'6px 14px', background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:'6px', cursor:'pointer', fontSize:'13px' }}>🗑 Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* preview modal */}
      {previewLayout && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={()=>setPreviewLayout(null)}>
          <div style={{ background:'#fff', borderRadius:'16px', maxWidth:'700px', width:'95%', maxHeight:'90vh', overflow:'auto', padding:'24px' }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h3 style={{ margin:0 }}>Preview: {previewLayout.layout_name}</h3>
              <button onClick={()=>setPreviewLayout(null)} style={{ background:'none', border:'none', fontSize:'24px', cursor:'pointer', color:'#6b7280' }}>✕</button>
            </div>
            <div style={{ overflow:'auto', border:'1px solid #e5e7eb', borderRadius:'8px' }} dangerouslySetInnerHTML={{ __html: previewHtml }}/>
          </div>
        </div>
      )}

      {/* edit modal */}
      {editLayout && (
        <EditModal
          layout={editLayout}
          onClose={()=>setEditLayout(null)}
          onSaved={()=>{ setEditLayout(null); loadLayouts(); }}
        />
      )}

      {/* toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:'20px', right:'20px', padding:'14px 24px', background:toast.type==='error'?'#ef4444':'#059669', color:'#fff', borderRadius:'10px', boxShadow:'0 4px 12px rgba(0,0,0,0.15)', zIndex:2000, fontWeight:500 }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default CertificateManagement;
